import { type FC } from 'react';
import { useIntl } from 'react-intl';

// MUI
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';

// screen
import type { MenuDishStatisticsResponse } from 'modules/dashboard/types';

interface Props {
  data?: MenuDishStatisticsResponse | null;
}

const BLUE = '#4C6FE7';
const ORANGE = '#F5A623';
const CONNECTOR_RED = '#E5533D';

const fmt = (n: number) => n.toLocaleString('vi-VN');

/** Vòng tròn dày 2 cung + tổng ở giữa lỗ. Cam (không chế biến) bắt đầu từ 12h, xanh phần còn lại. */
const Ring: FC<{ processing: number; packed: number; total: number }> = ({
  processing,
  packed,
  total,
}) => {
  const size = 220;
  const stroke = 58;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const sum = processing + packed || 1;
  const orangeLen = (packed / sum) * c;

  return (
    <Box sx={{ position: 'relative', width: size, height: size, flexShrink: 0 }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <g transform={`rotate(-90 ${size / 2} ${size / 2})`}>
          {/* Xanh phủ toàn bộ (phần chế biến chiếm đa số) */}
          <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={BLUE} strokeWidth={stroke} />
          {/* Cam vẽ đè từ 12h theo chiều kim đồng hồ (phần không chế biến) */}
          <circle
            cx={size / 2}
            cy={size / 2}
            r={r}
            fill="none"
            stroke={ORANGE}
            strokeWidth={stroke}
            strokeDasharray={`${orangeLen} ${c - orangeLen}`}
          />
        </g>
      </svg>
      {/* Tổng ở giữa lỗ */}
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Typography sx={{ fontSize: 11, color: 'text.secondary', lineHeight: 1 }}>Tổng</Typography>
        <Typography sx={{ fontWeight: 800, fontSize: 22, color: 'text.primary', lineHeight: 1.2 }}>
          {fmt(total)}
        </Typography>
      </Box>
    </Box>
  );
};

/** Hộp diễn giải: % lớn + nhãn + số tuyệt đối của thành phần đó (đặt ngay cạnh diễn giải). */
const CalloutBox: FC<{ percent: number; label: string; count: number; color: string; bg: string }> = ({
  percent,
  label,
  count,
  color,
  bg,
}) => (
  <Box sx={{ bgcolor: bg, borderRadius: 2, px: 2.5, py: 1.5, minWidth: 132, textAlign: 'center' }}>
    <Typography sx={{ color, fontWeight: 800, fontSize: 30, lineHeight: 1.05 }}>{percent}%</Typography>
    <Typography sx={{ color, fontSize: 15, fontWeight: 600 }}>{label}</Typography>
    <Typography sx={{ color: 'text.primary', fontWeight: 700, fontSize: 18, mt: 0.5 }}>
      {fmt(count)}
    </Typography>
  </Box>
);

const Connector: FC<{ color: string }> = ({ color }) => (
  <Box sx={{ width: 28, height: 2, bgcolor: color, flexShrink: 0 }} />
);

/** Biểu đồ tròn tỉ lệ món chế biến / không chế biến toàn hệ thống (global_catalog). */
const CatalogDonut: FC<Props> = ({ data }) => {
  const { formatMessage } = useIntl();
  const catalog = data?.global_catalog ?? null;
  const total = catalog ? catalog.total_processing_dishes + catalog.total_packed_dishes : 0;

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        borderRadius: 2.5,
        border: '1px solid #EEF0F4',
        boxShadow: '0 2px 10px rgba(16,24,40,0.06)',
      }}
    >
      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
        {formatMessage({ id: 'dashboard.catalog.sectionTitle' })}
      </Typography>
      {!catalog ? (
        <Typography variant="body2" sx={{ color: 'text.disabled', py: 6, textAlign: 'center' }}>
          {formatMessage({ id: 'dashboard.noData' })}
        </Typography>
      ) : (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexWrap: 'nowrap',
            gap: 0,
            py: 2,
            minHeight: 240,
          }}
        >
          {/* Hộp trái (chế biến) — số nằm ngay trong hộp */}
          <Box sx={{ display: 'flex', alignItems: 'center', alignSelf: 'center' }}>
            <CalloutBox
              percent={Math.round(catalog.processing_dish_ratio_percent)}
              label={formatMessage({ id: 'dashboard.catalog.processing' })}
              count={catalog.total_processing_dishes}
              color={BLUE}
              bg="#E8EEFC"
            />
            <Connector color={CONNECTOR_RED} />
          </Box>

          <Ring
            processing={catalog.processing_dish_ratio_percent}
            packed={catalog.packed_dish_ratio_percent}
            total={total}
          />

          {/* Hộp phải (không chế biến) — số nằm ngay trong hộp */}
          <Box sx={{ display: 'flex', alignItems: 'center', alignSelf: 'flex-start' }}>
            <Connector color={ORANGE} />
            <CalloutBox
              percent={Math.round(catalog.packed_dish_ratio_percent)}
              label={formatMessage({ id: 'dashboard.catalog.packed' })}
              count={catalog.total_packed_dishes}
              color={ORANGE}
              bg="#FDF3E2"
            />
          </Box>
        </Box>
      )}
    </Paper>
  );
};

export default CatalogDonut;
